import { useEffect, useState, useCallback } from "react";
import { Search } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Loader, fmt, StatusBadge, fmtDateTime, EmptyState } from "@/components/shared";
import { AdminPageHeader } from "./adminShared";

const filters = ["all", "active", "matured"];

export default function AdminInvestments() {
  const [data, setData] = useState(null);
  const [status, setStatus] = useState("active");
  const [search, setSearch] = useState("");
  const [q, setQ] = useState("");

  const load = useCallback(async () => {
    setData(null);
    const r = await api.get("/admin/investments", { params: { status, search: q || undefined } });
    setData(r.data);
  }, [status, q]);
  useEffect(() => { load(); }, [load]);
  const release = async (i) => { try { await api.post(`/admin/investments/${i.id}/release-principal`); toast.success("Principal released"); load(); } catch (e) { toast.error(formatApiError(e)); } };

  const totals = data?.totals || {};
  const cards = [
    ["Active Plans", totals.active_count, "text-cyan-300", "inv-total-active"],
    ["Matured", totals.matured_count, "text-emerald-300", "inv-total-completed"],
    ["Total Invested", `${fmt(totals.total_invested)} USDT`, "text-white", "inv-total-invested"],
    ["Locked Principal", `${fmt(totals.locked_principal)} USDT`, "text-white", "inv-total-locked"],
    ["Total ROI Generated", `${fmt(totals.total_paid_out)} USDT`, "text-emerald-300", "inv-total-paid"],
    ["Pending Release", totals.pending_release, "text-amber-300", "inv-total-pending"],
  ];

  return (
    <div data-testid="admin-investments">
      <AdminPageHeader eyebrow="Management" title="Investments" subtitle="Every user's active and completed plan cycles."
        right={<div className="flex gap-2 flex-wrap">{filters.map((f) => (
          <button key={f} data-testid={`investment-filter-${f}`} onClick={() => setStatus(f)}
            className={`px-4 py-2 rounded-lg text-sm border capitalize transition-colors ${status === f ? "bg-cyan-500/15 border-cyan-500/40 text-cyan-300" : "border-white/10 text-slate-400 hover:border-cyan-500/20"}`}>{f}</button>
        ))}</div>} />

      <p className="text-xs text-slate-500 uppercase tracking-wider mb-2">Platform-wide totals</p>
      <div className="grid grid-cols-2 lg:grid-cols-6 gap-4 mb-6">
        {cards.map(([label, val, cls, tid]) => (
          <div key={label} className="glass rounded-2xl p-5">
            <p className="text-xs text-slate-500 uppercase tracking-wider">{label}</p>
            <p className={`font-mono text-2xl font-bold mt-2 ${cls}`} data-testid={tid}>{val ?? "—"}</p>
          </div>
        ))}
      </div>

      <form onSubmit={(e) => { e.preventDefault(); setQ(search); }} className="flex gap-2 mb-4">
        <div className="relative flex-1 max-w-md">
          <Search className="w-4 h-4 text-slate-500 absolute left-3 top-1/2 -translate-y-1/2" />
          <input data-testid="investment-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search by user name or email"
            className="w-full bg-slate-900/60 border border-cyan-500/20 rounded-lg pl-9 pr-4 py-2 text-sm text-slate-200 focus:outline-none focus:border-cyan-400" />
        </div>
        <button type="submit" data-testid="investment-search-btn" className="glass glass-hover px-4 py-2 rounded-lg text-sm text-cyan-300">Search</button>
      </form>

      {data === null ? <Loader /> : data.items.length ? (
        <div className="glass rounded-2xl p-4 overflow-x-auto">
          <table className="w-full text-sm min-w-[1100px]">
            <thead><tr className="text-left text-slate-500 border-b border-white/5">
              {["User", "Plan", "Principal", "Daily ROI", "Working days", "ROI earned", "Remaining ROI", "Started", "Next ROI / Maturity", "Status", ""].map((h) => <th key={h} className="pb-3 px-2 font-medium">{h}</th>)}
            </tr></thead>
            <tbody>
              {data.items.map((i) => (
                <tr key={i.id} className="border-b border-white/5 last:border-0" data-testid={`admin-investment-row-${i.id}`}>
                  <td className="py-3 px-2"><p className="text-slate-200">{i.user_name}</p><p className="text-xs text-slate-500">{i.user_email}</p></td>
                  <td className="py-3 px-2 text-slate-300">{i.plan_name}</td>
                  <td className="py-3 px-2 font-mono text-white">{fmt(i.amount)}</td>
                  <td className="py-3 px-2 font-mono text-cyan-300">{i.daily_percentage}% · {fmt(i.daily_roi)}</td>
                  <td className="py-3 px-2 min-w-[130px]">
                    <div className="flex items-center gap-2">
                      <div className="flex-1 h-1.5 rounded-full bg-slate-800 overflow-hidden"><div className="h-full bg-gradient-to-r from-cyan-400 to-blue-500" style={{ width: `${i.progress}%` }} /></div>
                      <span className="text-xs text-slate-400 font-mono">{i.days_credited}/{i.duration_days}</span>
                    </div>
                  </td>
                  <td className="py-3 px-2 font-mono text-emerald-300">{fmt(i.roi_earned)}</td>
                  <td className="py-3 px-2 font-mono text-slate-300">{fmt(i.remaining_roi)}</td>
                  <td className="py-3 px-2 text-xs text-slate-500">{fmtDateTime(i.started_at)}</td>
                  <td className="py-3 px-2 text-xs text-slate-500">{i.status === "active" ? <>{fmtDateTime(i.next_credit_at)}<br /><span className="text-slate-600">matures {i.maturity_date ? i.maturity_date.slice(0, 10) : "—"}</span></> : fmtDateTime(i.completed_at)}</td>
                  <td className="py-3 px-2"><StatusBadge status={i.status === "completed" ? "matured" : i.status} /></td>
                  <td className="py-3 px-2">{i.status === "matured" && i.maturity_rule === "hold_principal" && !i.principal_released && <button onClick={() => release(i)} data-testid={`release-${i.id}`} className="text-xs px-3 py-1.5 rounded-lg bg-amber-500/15 text-amber-300 border border-amber-500/30 hover:bg-amber-500/25 whitespace-nowrap">Release principal</button>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : <EmptyState title="No investments" subtitle={`No ${status === "all" ? "" : status} investments found${q ? ` for "${q}"` : ""}.`} />}
    </div>
  );
}
